import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axiosInstance from "../api/axiosInstance";
import "./EmailConfirmation.css";

const EmailConfirmation = () => {
  const [statusMessage, setStatusMessage] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [showResend, setShowResend] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const userId = searchParams.get("UserId") || searchParams.get("userId");
  const token = searchParams.get("token");
  const email = searchParams.get("email");

  useEffect(() => {
    const confirmEmail = async () => {
      if (!userId || !token) {
        setStatusMessage("Invalid confirmation link - missing parameters.");
        setShowResend(true);
        setIsLoading(false);
        return;
      }

      try {
        // Don't encode the token again - it's already URL-encoded
        const response = await axiosInstance.get(
          `/User/ConfirmEmail?userId=${userId}&token=${token}`,
          { 
            headers: { 
              Authorization: undefined,
              'Accept': 'text/plain' // Expect text response
            } 
          }
        );

        // Handle plain text response
        const message = typeof response.data === 'string' 
          ? response.data 
          : "Email confirmed successfully!";
          
        setStatusMessage(message);
        
        // Only redirect if successful
        if (response.status === 200) {
          setTimeout(() => navigate("/login", { 
            state: { emailConfirmed: true } 
          }), 3000);
        }
      } catch (error) {
        let errorMessage = "An error occurred while confirming your email.";
        
        if (error.response) {
          // Handle text or JSON error responses
          errorMessage = typeof error.response.data === 'string'
            ? error.response.data
            : error.response.data?.message || errorMessage;
        }
        
        setStatusMessage(errorMessage);
        setShowResend(true);
      } finally {
        setIsLoading(false);
      }
    };

    confirmEmail();
  }, [userId, token, navigate]);

  const handleResendEmail = async () => {
    if (!email) {
      setStatusMessage("No email found to resend confirmation.");
      return;
    }

    try {
      setIsLoading(true);
      const response = await axiosInstance.post("/auth/resend-confirmation", {
        email: email,
      });
      setStatusMessage(response.data.message || "Confirmation email resent successfully!");
    } catch (error) {
      setStatusMessage(
        error.response?.data?.message || 
        "Failed to resend confirmation email. Please try again later."
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="confirmation-wrapper">
      <div className="confirmation-card">
        <h1>Email Confirmation</h1>
        {isLoading ? (
          <div className="loading-state">
            <p className="loading-text">Confirming your email...</p>
            <div className="spinner"></div>
          </div>
        ) : (
          <>
            <p className={`status-message ${showResend ? 'error' : 'success'}`}>
              {statusMessage}
            </p>
            <div className="button-group">
              {showResend && email && (
                <button 
                  className="btn resend" 
                  onClick={handleResendEmail}
                  disabled={isLoading}
                >
                  {isLoading ? "Sending..." : "Resend Confirmation"}
                </button>
              )}
              <button 
                className="btn login" 
                onClick={() => navigate("/login")}
              >
                Go to Login
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default EmailConfirmation;